<template>
  <div class="tw:transition-colors tw:rounded-sm tw:p-3 tw:border tw:group" :class="actionStyles" @click="handleClick">
    <div v-if="metricType === 'checkbox'" class="tw:mt-1 tw:self-baseline">
      <EdroneCheckbox ref="checkbox" :checked="modelValue" :value="modelValue" @change.stop="handleClick" />
    </div>
    <div class="tw:flex tw:flex-col tw:gap-2 tw:w-full">
      <div v-if="symbolType" class="tw:flex">
        <div
          class="tw:border tw:size-10 tw:flex tw:items-center tw:justify-center tw:rounded-sm"
          :class="symbolStyles.symbol"
        >
          <FontAwesomeIcon :icon="symbolStyles.icon" class="tw:size-5" :class="symbolStyles.iconColor" />
        </div>

        <div class="tw:flex tw:gap-1 tw:ml-auto">
          <EdroneMeter v-if="indumeter" v-bind="indumeter" />
          <slot name="header-actions"></slot>
        </div>
      </div>

      <div class="tw:flex tw:flex-col tw:gap-1">
        <div class="tw:flex tw:flex-col tw:gap-3">
          <div class="tw:flex tw:max-h-5">
            <div v-if="title" class="tw:flex tw:gap-1 tw:items-center tw:min-w-0">
              <p
                class="tw:body-2 tw:!text-default-700 tw:overflow-hidden tw:overflow-ellipsis tw:whitespace-nowrap tw:min-w-0 tw:flex-1"
              >
                {{ title }}
              </p>
              <EdroneTooltip v-if="titleTooltip" :helper="titleTooltip" trigger-event="mouseenter click" />
            </div>

            <div v-if="$slots['header-actions'] && !symbolType" class="tw:flex tw:shrink-0 tw:gap-1 tw:ml-auto">
              <EdroneMeter v-if="indumeter" v-bind="indumeter" />
              <slot name="header-actions"></slot>
            </div>
          </div>

          <p v-if="subtitle" class="tw:caption tw:!text-default-700">{{ subtitle }}</p>
        </div>

        <div class="tw:flex tw:gap-2 tw:flex-col">
          <div class="tw:flex tw:gap-2 tw:items-center">
            <p class="tw:heading-1" :class="{ 'tw:heading-2': isDense }">{{ value }}</p>
            <EdroneBadge v-if="badgeText" :label="badgeText" size="small" />
          </div>

          <div v-if="previousValue">
            <div class="tw:flex tw:gap-2 tw:items-center">
              <EdroneBadge :label="previousValue" size="small" />
              <p class="tw:caption">{{ trans._common.vsPrev30Days }}</p>
            </div>
          </div>
        </div>
      </div>

      <div v-if="bottomMetrics" class="tw:border-t tw:flex tw:w-full tw:transition-colors" :class="borderStyles">
        <div
          v-for="bottomMetric in bottomMetrics"
          :key="bottomMetric.metricName"
          class="tw:pt-3 tw:px-4 tw:flex tw:gap-2 tw:flex-col tw:last-of-type:border-r-0 tw:border-r tw:w-1/3 tw:flex-1 tw:transition-colors"
          :class="borderStyles"
        >
          <div class="tw:flex tw:gap-1 tw:items-center">
            <p class="tw:body-2 tw:!text-default-700 tw:whitespace-nowrap tw:overflow-hidden tw:text-ellipsis">
              {{ bottomMetric.metricName }}
            </p>
            <EdroneTooltip
              v-if="bottomMetric.tooltip"
              trigger-event="mouseenter click"
              :helper="bottomMetric.tooltip"
            />
          </div>

          <p class="tw:body-2 tw:whitespace-nowrap tw:overflow-hidden tw:text-ellipsis">{{ bottomMetric.value }}</p>
        </div>
      </div>
    </div>
  </div>
</template>

<script lang="ts" setup>
import { FontAwesomeIcon } from "@fortawesome/vue-fontawesome";
import EdroneTooltip from "edrone-components/EdroneTooltip.vue";
import EdroneBadge from "edrone-components/EdroneBadge/EdroneBadge.vue";
import EdroneMeter from "edrone-components/EdroneMeter.vue";
import { computed } from "vue";
import EdroneCheckbox from "edrone-components/EdroneCheckbox.vue";

type SymbolType = "success" | "warning" | "error" | "info" | "neutral";
type MetricType = "default" | "checkbox";

interface Props {
  title: string;
  subtitle?: string;
  value: string | number;
  previousValue?: string;
  symbolType?: SymbolType;
  hasIndumeter?: boolean;
  indumeter?: {
    industryAverage: number;
    industryFormat: string;
    metric: number;
    step: number;
    steps: { step: number; minValue: number; maxValue: number }[];
  };
  isSelected?: boolean;
  badgeText?: string;
  bottomMetrics?: {
    metricName: string;
    value: string;
    tooltip?: string;
  }[];
  titleTooltip?: string;
  dense?: boolean;
  metricType?: MetricType;
}
const props = withDefaults(defineProps<Props>(), {
  subtitle: "",
  previousValue: "",
  symbolType: undefined,
  indumeter: undefined,
  hasIndumeter: true,
  badgeText: "",
  bottomMetrics: undefined,
  titleTooltip: "",
  dense: false,
  metricType: "default",
  isSelected: false,
});
const modelValue = defineModel<boolean>("modelValue", {
  default: undefined,
});

const handleClick = () => {
  if (modelValue.value === undefined) {
    return;
  }

  modelValue.value = !modelValue.value;
};

const symbolStyles = computed(() => {
  return {
    success: {
      symbol: "tw:bg-green-000 tw:border-green-200",
      icon: "fa-solid fa-bolt",
      iconColor: "tw:text-green-500",
    },
    neutral: {
      symbol: "tw:bg-teal-000 tw:border-teal-200",
      icon: "fa-solid fa-bolt",
      iconColor: "tw:text-teal-500",
    },
    warning: {
      symbol: "tw:bg-yellow-000 tw:border-yellow-200",
      icon: "fa-solid fa-bolt",
      iconColor: "tw:text-yellow-500",
    },
    error: {
      symbol: "tw:bg-red-000 tw:border-red-200",
      icon: "fa-solid fa-xmark",
      iconColor: "tw:text-red-500",
    },
    info: {
      symbol: "tw:bg-blue-000 tw:border-blue-200",
      icon: "fa-solid fa-circle-info",
      iconColor: "tw:text-blue-500",
    },
  }[props.symbolType || "success"];
});

const isDense = computed(() => {
  return props.dense || props.metricType === "checkbox";
});

const actionStyles = computed(() => {
  return {
    "tw:flex tw:gap-2": props.metricType === "checkbox",
    "tw:bg-default-000 tw:border-default-000": modelValue.value === undefined,
    "tw:bg-white tw:hover:border-blue-500 tw:border-default-200 tw:cursor-pointer":
      modelValue.value !== undefined && !modelValue.value,
    "tw:bg-blue-000 tw:border-blue-500 tw:hover:bg-blue-100 tw:hover:border-blue-600 tw:cursor-pointer":
      modelValue.value === true,
  };
});

const borderStyles = computed(() => {
  return {
    "tw:border-default-200": modelValue.value === undefined,
    "tw:group-hover:border-blue-500 tw:border-default-200": modelValue.value !== undefined && !modelValue.value,
    "tw:border-blue-500 tw:group-hover:border-blue-600 ": modelValue.value === true,
  };
});
</script>
